import { useCallback, useEffect, useState } from "react";
import Queries from "../params/queries";
import Mutations from "../params/mutations";
import type { AccountRow, FetchApi, PhoneRow } from "../params/types";

interface PhonesProps {
  accounts?: AccountRow[];
  fetchApi?: FetchApi;
  setError?: (message: string) => void;
  flashSuccess?: (message: string) => void;
}

function phoneTitle(phone: PhoneRow): string {
  const model = String(phone.model || "").trim();
  if (model) return model;
  const id = String(phone.id || "");
  if (id.startsWith("ip:")) return id.slice(3);
  return id;
}

function phoneHint(phone: PhoneRow): string {
  const id = String(phone.id || "");
  if (!id) return "";
  if (id.startsWith("ip:")) return id;
  return id.length > 10 ? id.slice(-8) : id;
}

function accountLabel(account: AccountRow): string {
  const ig = String(account.ig_username || "").replace(/^@/, "");
  return ig ? `@${ig}` : account.username;
}

export default function Phones({
  accounts = [],
  fetchApi,
  setError,
  flashSuccess,
}: PhonesProps) {
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [binding, setBinding] = useState("");

  const reload = useCallback(() => {
    if (!fetchApi) return undefined;
    let cancelled = false;
    fetchApi(Queries.Phones).then((res) => {
      if (cancelled) return;
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to load phones");
      } else {
        setPhones(res?.phones || []);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchApi, setError]);

  useEffect(() => {
    const cancel = reload();
    const timer = setInterval(() => reload(), 4000);
    return () => {
      cancel?.();
      clearInterval(timer);
    };
  }, [reload]);

  const choicesFor = (phone: PhoneRow): AccountRow[] => {
    const taken = new Map<string, string>();
    for (const row of phones) {
      const username = String(row.account || "");
      if (username) taken.set(username, row.id);
    }
    const choices = accounts.filter((account) => {
      if (account.ai?.browser_mode !== "native") return false;
      const owner = taken.get(account.username);
      return !owner || owner === phone.id;
    });
    const current = String(phone.account || "");
    if (current && !choices.some((account) => account.username === current)) {
      const row = accounts.find((account) => account.username === current);
      if (row) choices.unshift(row);
    }
    return choices;
  };

  const onBind = async (phone: PhoneRow, username: string) => {
    if (!fetchApi || binding) return;
    setBinding(phone.id);
    setError?.("");
    try {
      const res = await fetchApi(Mutations.BindPhone, {
        phone_id: phone.id,
        username,
      });
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to assign the phone");
        return;
      }
      flashSuccess?.(username ? "Phone assigned" : "Phone cleared");
      reload();
    } finally {
      setBinding("");
    }
  };

  return (
    <section className="ib-card">
      <div className="ib-card-header">
        <h2>Phones</h2>
      </div>
      {loading && !phones.length ? (
        <div className="ib-empty">Loading phones…</div>
      ) : !phones.length ? (
        <div className="ib-empty">
          No phones are connected, and no account is bound to a phone.
        </div>
      ) : (
        <div className="ib-table ib-table--phones">
          <div className="ib-table__head">
            <span>Phone</span>
            <span>Address</span>
            <span>Link</span>
            <span>Proxy</span>
            <span>Screen</span>
            <span>Account</span>
          </div>
          {phones.map((phone) => {
            const online = Boolean(phone.connected);
            return (
              <div className="ib-table__row" key={phone.id}>
                <span>
                  {phoneTitle(phone)}
                  <span className="ib-muted"> {phoneHint(phone)}</span>
                </span>
                <span>{phone.address || "—"}</span>
                <span>{online ? "Connected" : "Offline"}</span>
                <span>{online ? (phone.proxy === "on" ? "On" : "Off") : "—"}</span>
                <span>
                  {online ? (phone.accessibility ? "On" : "Off") : "—"}
                </span>
                <span>
                  <select
                    className={
                      phone.account
                        ? "ib-phones-select"
                        : "ib-phones-select ib-phones-select--empty"
                    }
                    value={phone.account || ""}
                    disabled={Boolean(binding)}
                    onChange={(event) => onBind(phone, event.target.value)}
                  >
                    <option value="">Not assigned</option>
                    {choicesFor(phone).map((account) => (
                      <option key={account.username} value={account.username}>
                        {accountLabel(account)}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
